import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import PanelLayout from '../components/PanelLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import {
  Activity, AlertTriangle, CheckCircle2, XCircle, Clock, Send, RefreshCw,
  Zap, Calendar, ChevronDown, ChevronUp, Server
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BLOCK_LABELS = {
  CAMPAIGN_NOT_FOUND: { label: 'Kampania nie znaleziona', color: 'red', desc: 'Post powiązany z nieistniejącą kampanią' },
  CAMPAIGN_PAUSED: { label: 'Kampania wstrzymana', color: 'amber', desc: 'Kampania ma status "paused" — wznów kampanię żeby publikować' },
  AUTO_PUBLISH_DISABLED: { label: 'Auto-publikacja wyłączona', color: 'red', desc: 'Użytkownik wyłączył auto-publikację w ustawieniach' },
  NO_FB_PAGE_CONNECTED: { label: 'Brak strony FB', color: 'red', desc: 'Brak aktywnego połączenia ze stroną Facebook' },
  FB_TOKEN_MISSING: { label: 'Brak tokenu FB', color: 'red', desc: 'Token dostępu do strony FB jest pusty — wymagane ponowne połączenie' },
  FB_TOKEN_SUSPICIOUS: { label: 'Token FB podejrzany', color: 'amber', desc: 'Token wygląda na nieprawidłowy (za krótki)' },
  DAILY_LIMIT_REACHED: { label: 'Limit dzienny osiągnięty', color: 'amber', desc: 'Opublikowano już 3 posty dzisiaj — post przesunięty na jutro' },
  MIN_INTERVAL_4H: { label: 'Za krótki interwał', color: 'amber', desc: 'Minimalny odstęp między postami to 4 godziny' },
  DUPLICATE_TEXT_24H: { label: 'Duplikat tekstu', color: 'amber', desc: 'Ten sam tekst został opublikowany w ciągu 24h' },
  NO_BLOCK_DETECTED: { label: 'Brak blokady', color: 'emerald', desc: 'Post powinien zostać opublikowany w następnym cyklu schedulera' },
};

function StatusBadge({ alive }) {
  return (
    <span data-testid="scheduler-status-badge" className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${alive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
      <span className={`w-2 h-2 rounded-full ${alive ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
      {alive ? 'AKTYWNY' : 'NIEAKTYWNY'}
    </span>
  );
}

function TimeDisplay({ label, timeObj }) {
  if (!timeObj) return <span className="text-zinc-300 text-[12px]">—</span>;
  const warsaw = typeof timeObj === 'object' ? timeObj.warsaw : timeObj;
  const utc = typeof timeObj === 'object' ? timeObj.utc : null;
  return (
    <div>
      <span className="text-[12px] text-zinc-500">{label}: </span>
      <span className="text-[12px] font-mono font-semibold text-zinc-800">{warsaw}</span>
      {utc && <span className="text-[10px] text-zinc-400 ml-1">(UTC: {new Date(utc).toISOString().slice(11, 19)})</span>}
    </div>
  );
}

function BlockBadge({ block }) {
  const info = BLOCK_LABELS[block.reason] || { label: block.reason, color: 'zinc', desc: block.detail };
  const colors = {
    red: 'bg-red-50 border-red-200 text-red-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    zinc: 'bg-zinc-50 border-zinc-200 text-zinc-700',
  };
  return (
    <div data-testid={`block-${block.reason}`} className={`flex items-start gap-2.5 p-3 rounded-xl border ${colors[info.color]}`}>
      {info.color === 'emerald' ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
      <div>
        <p className="text-[13px] font-bold">{info.label}</p>
        <p className="text-[11px] opacity-70">{info.desc}</p>
        {block.detail && block.detail !== info.desc && <p className="text-[10px] opacity-50 mt-0.5 font-mono">{block.detail}</p>}
      </div>
    </div>
  );
}

export default function AdminAutoPublishDebug() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [showUpcoming, setShowUpcoming] = useState(false);
  const [showAttempts, setShowAttempts] = useState(false);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/auto-publish-debug`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (err) {
      toast.error('Błąd ładowania diagnostyki');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAction = async (action, postId) => {
    setActionLoading(p => ({ ...p, [postId]: action }));
    try {
      const res = await axios.post(`${API}/admin/auto-publish-${action}/${postId}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast[res.data.success ? 'success' : 'error'](res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Błąd');
    }
    setActionLoading(p => ({ ...p, [postId]: null }));
  };

  return (
    <PanelLayout>
      <div className="max-w-[900px] mx-auto px-4 py-6 space-y-5" data-testid="admin-auto-publish-debug">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-zinc-900">Auto-Publish Debug</h1>
            <p className="text-[12px] text-zinc-400">Diagnostyka automatycznej publikacji</p>
          </div>
          <button onClick={fetchData} disabled={loading} className="p-2 rounded-lg bg-zinc-100 hover:bg-zinc-200 transition-colors" data-testid="refresh-debug">
            <RefreshCw className={`w-4 h-4 text-zinc-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {loading && !data ? (
          <div className="text-center py-12 text-zinc-400">Ładowanie diagnostyki...</div>
        ) : data ? (
          <>
            {/* Server Time */}
            <div className="bg-zinc-50 rounded-xl p-4 border border-zinc-200" data-testid="server-time">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-zinc-500" />
                <span className="text-[12px] font-bold text-zinc-600 uppercase tracking-wider">Czas serwera</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] text-zinc-400">Warsaw</p>
                  <p className="text-[14px] font-mono font-bold text-zinc-800">{data.server_time.warsaw}</p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-400">UTC</p>
                  <p className="text-[14px] font-mono text-zinc-500">{new Date(data.server_time.utc).toISOString().slice(0, 19)}</p>
                </div>
              </div>
            </div>

            {/* Schedulers Status */}
            <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden" data-testid="schedulers-status">
              <div className="p-4 border-b border-zinc-100">
                <div className="flex items-center gap-2">
                  <Server className="w-4 h-4 text-zinc-500" />
                  <span className="text-[13px] font-bold text-zinc-800">Status schedulerów</span>
                </div>
              </div>
              <div className="divide-y divide-zinc-100">
                {data.schedulers.map(s => (
                  <div key={s.name} className="p-4" data-testid={`scheduler-${s.name}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="text-[13px] font-bold text-zinc-800">{s.name === 'fb_auto_publisher' ? 'FB Auto Publisher' : 'Scheduled Posts'}</span>
                        <span className="text-[11px] text-zinc-400 ml-2">({s.collection})</span>
                      </div>
                      <StatusBadge alive={s.alive} />
                    </div>
                    <p className="text-[11px] text-zinc-400 mb-3">{s.description}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px]">
                      <TimeDisplay label="Heartbeat" timeObj={s.last_heartbeat_warsaw ? { warsaw: s.last_heartbeat_warsaw } : null} />
                      <TimeDisplay label="Ostatni sukces" timeObj={s.last_successful_publish} />
                      <TimeDisplay label="Ostatni błąd" timeObj={s.last_failed_publish} />
                    </div>
                    {s.last_failed_reason && (
                      <p className="text-[11px] text-red-500 mt-1">Powód: {s.last_failed_reason}</p>
                    )}
                    {s.last_skip_reason && (
                      <p className="text-[11px] text-amber-500 mt-1">Ostatni skip: {s.last_skip_reason}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Source of Truth */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4" data-testid="source-of-truth">
              <p className="text-[12px] font-bold text-blue-800 mb-2">Source of Truth</p>
              <div className="space-y-1 text-[12px] text-blue-700">
                <p><strong>Kampanie FB:</strong> {data.source_of_truth.kampanie_fb}</p>
                <p><strong>Ręczne posty:</strong> {data.source_of_truth.reczne_posty}</p>
                <p className="text-[11px] text-blue-500">{data.source_of_truth.uwaga}</p>
              </div>
            </div>

            {/* Due Posts (Today's blocked posts) */}
            <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden" data-testid="due-posts">
              <div className="p-4 border-b border-zinc-100">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  <span className="text-[13px] font-bold text-zinc-800">
                    Zaległe posty
                    {data.due_posts.booster_posts_overdue > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 text-[10px] rounded-full font-bold">
                        {data.due_posts.booster_posts_overdue} zaległych
                      </span>
                    )}
                  </span>
                </div>
              </div>
              <div className="p-4">
                {data.due_posts.details.length === 0 ? (
                  <div className="flex items-center gap-2 text-emerald-600">
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="text-[13px]">Brak zaległych postów — wszystko OK</span>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {data.due_posts.details.map(post => (
                      <div key={post.post_id} className="border border-zinc-200 rounded-xl p-4" data-testid={`post-${post.post_id}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <span className="text-[13px] font-bold text-zinc-800">Post #{post.day_index || '?'}</span>
                            <span className={`ml-2 px-2 py-0.5 text-[10px] rounded-full font-bold ${post.status === 'scheduled' ? 'bg-amber-100 text-amber-700' : post.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                              {post.status}
                            </span>
                          </div>
                          <span className="text-[10px] text-zinc-400 font-mono">{post.post_id.slice(0, 8)}...</span>
                        </div>
                        
                        {/* Times */}
                        <div className="grid grid-cols-2 gap-2 mb-3 text-[11px]">
                          <div>
                            <span className="text-zinc-400">Planowany (Warsaw):</span>
                            <p className="font-mono font-semibold text-zinc-800">{post.publish_at_warsaw || '—'}</p>
                          </div>
                          <div>
                            <span className="text-zinc-400">Planowany (UTC):</span>
                            <p className="font-mono text-zinc-500">{post.publish_at_utc ? new Date(post.publish_at_utc).toISOString().slice(0, 19) : '—'}</p>
                          </div>
                        </div>

                        {post.overdue_minutes && (
                          <p className="text-[11px] text-red-500 font-semibold mb-3">
                            Opóźniony o {post.overdue_minutes > 60 ? `${Math.floor(post.overdue_minutes / 60)}h ${post.overdue_minutes % 60}min` : `${post.overdue_minutes} min`}
                          </p>
                        )}

                        {post.last_error && (
                          <p className="text-[11px] text-red-500 bg-red-50 px-3 py-2 rounded-lg mb-3 font-mono">{post.last_error}</p>
                        )}

                        {/* Blocks */}
                        <div className="space-y-2 mb-3">
                          {post.blocks.map((block, i) => (
                            <BlockBadge key={i} block={block} />
                          ))}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleAction('retry', post.post_id)}
                            disabled={actionLoading[post.post_id] === 'retry'}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white text-[11px] font-bold rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50"
                            data-testid={`retry-${post.post_id}`}
                          >
                            <RefreshCw className={`w-3 h-3 ${actionLoading[post.post_id] === 'retry' ? 'animate-spin' : ''}`} />
                            Retry
                          </button>
                          <button
                            onClick={() => handleAction('force', post.post_id)}
                            disabled={actionLoading[post.post_id] === 'force'}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white text-[11px] font-bold rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                            data-testid={`force-${post.post_id}`}
                          >
                            <Zap className={`w-3 h-3 ${actionLoading[post.post_id] === 'force' ? 'animate-spin' : ''}`} />
                            Force Publish
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Upcoming 24h */}
            <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden" data-testid="upcoming-posts">
              <button onClick={() => setShowUpcoming(!showUpcoming)} className="w-full p-4 flex items-center justify-between hover:bg-zinc-50 transition-colors">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-zinc-500" />
                  <span className="text-[13px] font-bold text-zinc-800">Nadchodzące (24h)</span>
                  <span className="text-[11px] text-zinc-400">{data.upcoming_24h.length} postów</span>
                </div>
                {showUpcoming ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
              </button>
              {showUpcoming && (
                <div className="border-t border-zinc-100 p-4">
                  {data.upcoming_24h.length === 0 ? (
                    <p className="text-[12px] text-zinc-400">Brak zaplanowanych postów na 24h</p>
                  ) : (
                    <div className="space-y-2">
                      {data.upcoming_24h.map((p, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                          <div>
                            <span className="text-[12px] font-semibold text-zinc-700">Post #{p.day_index || '?'}</span>
                            <span className="text-[10px] text-zinc-400 ml-2 font-mono">{p.id?.slice(0, 8)}...</span>
                          </div>
                          <span className="text-[12px] font-mono font-semibold text-zinc-800">{p.publish_at_warsaw}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Recent Attempts */}
            <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden" data-testid="recent-attempts">
              <button onClick={() => setShowAttempts(!showAttempts)} className="w-full p-4 flex items-center justify-between hover:bg-zinc-50 transition-colors">
                <div className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-zinc-500" />
                  <span className="text-[13px] font-bold text-zinc-800">Historia prób (24h)</span>
                  <span className="text-[11px] text-zinc-400">{data.recent_attempts_24h.length} wpisów</span>
                </div>
                {showAttempts ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
              </button>
              {showAttempts && (
                <div className="border-t border-zinc-100 p-4">
                  {data.recent_attempts_24h.length === 0 ? (
                    <p className="text-[12px] text-zinc-400">Brak prób w ciągu 24h</p>
                  ) : (
                    <div className="space-y-2">
                      {data.recent_attempts_24h.map((a, i) => (
                        <div key={i} className={`flex items-center gap-3 p-3 rounded-lg ${a.success ? 'bg-emerald-50' : a.action === 'skipped' ? 'bg-amber-50' : 'bg-red-50'}`}>
                          {a.success ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : a.action === 'skipped' ? <Clock className="w-4 h-4 text-amber-500 shrink-0" /> : <XCircle className="w-4 h-4 text-red-500 shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <span className="text-[11px] font-semibold">{a.scheduler_name}</span>
                            <span className="text-[10px] text-zinc-400 ml-1">post: {a.post_id?.slice(0, 8)}...</span>
                            <p className="text-[10px] text-zinc-500 truncate">{a.reason}</p>
                          </div>
                          <span className="text-[10px] text-zinc-400 font-mono shrink-0">
                            {a.timestamp ? new Date(a.timestamp).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : null}
      </div>
    </PanelLayout>
  );
}
